import { ComponentPropsWithoutRef, PropsWithChildren } from "react";
import { twMerge } from "tailwind-merge";

import { Typography } from "~/common/components";
import { TYPOGRAPHY_VARIANTS } from "~/common/utils";
import { ACTIVITY_FEED_CODES } from "~/providers/constants";
import { useGetActivityFeedCardData } from "~/providers/hooks/patientInteractions/useGetActivityFeedCardData";
import { formatDate, getFullName } from "~/providers/utils";
import { PatientFeed } from "~/providers/utils/types/interactions";
import { ConsentAcknowledgedFeed } from "~/providers/utils/types/patientFeeds";
import {
  PatientMedicationDetailsDrawerProps,
  SelectedPatientCheckin,
} from "../ActivityFeed";
import { RenderFeedCardChildren } from "./RenderFeedCardChildren";

interface ActivityFeedCardProps
  extends ComponentPropsWithoutRef<"div">, PropsWithChildren {
  feedOption: PatientFeed;
  viewSignedConsents: (signedConsentsActivity: ConsentAcknowledgedFeed) => void;
  viewPatientCheckInDetails: (
    selectedPatientCheckinInfo: SelectedPatientCheckin,
  ) => void;
  viewMedicationDetails: (
    medication: PatientMedicationDetailsDrawerProps,
  ) => void;
}

export const ActivityFeedCard = ({
  feedOption,
  viewSignedConsents,
  viewPatientCheckInDetails,
  viewMedicationDetails,
  ...props
}: ActivityFeedCardProps) => {
  const {
    code,
    generated_by_name: generatedBy,
    patient_generated: patientGenerated,
    created_at,
  } = feedOption;

  const formattedDate = formatDate(created_at);

  const { getFeedData } = useGetActivityFeedCardData({
    viewSignedConsents,
    viewPatientCheckInDetails,
    viewMedicationDetails,
  });
  const feedData = getFeedData(feedOption);

  const {
    title,
    description,
    icon,
    internalNote,
    outboundNote,
    actions,
    extraInfo,
  } = {
    ...feedData,
  };

  if (!title) return null;

  const getActivityAttributionName = () => {
    const context = feedOption.context;

    if (patientGenerated && context && "data" in context) {
      const feedData = context.data;
      if (
        feedData &&
        typeof feedData === "object" &&
        "organization_patient" in feedData
      ) {
        const organizationPatient = feedData.organization_patient as {
          patient?: { name?: string; last_name?: string };
        };
        return getFullName(
          organizationPatient.patient?.name ?? generatedBy,
          organizationPatient.patient?.last_name ?? "",
        );
      }
    }

    if (
      (code === ACTIVITY_FEED_CODES.primary_provider_changed ||
        code === ACTIVITY_FEED_CODES.consultation_terminated ||
        code === ACTIVITY_FEED_CODES.consultation_re_opened) &&
      context &&
      "action_provider_info" in context
    ) {
      return getFullName(
        context.action_provider_info?.name ?? generatedBy,
        context.action_provider_info?.last_name ?? "",
      );
    }

    if (context && "data" in context) {
      const feedData = context.data;
      if (
        feedData &&
        typeof feedData === "object" &&
        "consultation" in feedData
      ) {
        const consultationData = feedData.consultation as {
          organization_team_member?: {
            team_member?: { name?: string; last_name?: string };
          };
        };
        return getFullName(
          consultationData.organization_team_member?.team_member?.name ??
            generatedBy,
          consultationData.organization_team_member?.team_member?.last_name ??
            "",
        );
      }
    }

    return generatedBy;
  };

  return (
    <div
      className={twMerge(
        "flex size-full flex-col gap-4 divide-y rounded-md border border-neutral-80 p-4 text-gray-70",
        patientGenerated &&
          "divide-primary-200 border-primary-200 bg-primary-50",
      )}
    >
      <div className={"flex size-full flex-row items-center gap-2"} {...props}>
        <div className="flex h-full items-start">{icon?.()}</div>
        <div className="flex w-full flex-col gap-1">
          <div className="flex w-full justify-between">
            <Typography className="font-medium">
              {getActivityAttributionName()}
            </Typography>

            {actions}
          </div>
          <div className="flex w-full justify-between">
            <div className="flex items-center">
              <Typography
                className={twMerge(
                  "text-base",
                  (code === ACTIVITY_FEED_CODES.prescription_paused ||
                    code === ACTIVITY_FEED_CODES.checkin_missed) &&
                    "text-orange-70",
                )}
              >
                {title}
              </Typography>
              {description && (
                <>
                  <span className="px-1 font-light text-neutral-80">|</span>
                  <Typography className="text-base first-letter:uppercase">
                    {description}
                  </Typography>
                </>
              )}
              {extraInfo && (
                <>
                  <span className="px-1 font-light text-neutral-80">|</span>
                  <Typography className="text-base first-letter:uppercase">
                    {extraInfo}
                  </Typography>
                </>
              )}
            </div>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.secondary}
              className="text-base font-light"
            >
              {formattedDate}
            </Typography>
          </div>
        </div>
      </div>
      <RenderFeedCardChildren
        feedOption={feedOption}
        internalNote={internalNote}
        outboundNote={outboundNote}
      />
    </div>
  );
};
